<div class="container mt-4 mb-4">
  <div class="card p-3">
    <h4 class="mb-3">Guest Access</h4>

    @if (isLoadingSavedCredentials()) {
      <div class="text-center py-4">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span class="ms-2">Loading saved credentials...</span>
      </div>
    }

    @if (!isLoadingSavedCredentials() && !guestAuthenticated()) {
      <div class="mb-3">
        <div class="btn-group w-100" role="group" aria-label="Guest request type">
          <button type="button" class="btn" [ngClass]="requestType() === 'existing' ? 'btn-primary' : 'btn-outline-primary'" (click)="selectRequestType('existing')">
            Existing Guest
          </button>
          <button type="button" class="btn" [ngClass]="requestType() === 'new' ? 'btn-primary' : 'btn-outline-primary'" (click)="selectRequestType('new')">
            New Guest
          </button>
        </div>
      </div>

      @if (requestType() === 'existing') {
        <form [formGroup]="guestForm" (ngSubmit)="continueGuestAccess()">
          <div class="mb-3">
            <label class="form-label" for="guestEmail">Email Address</label>
            <input id="guestEmail" class="form-control" formControlName="email" placeholder="Enter email address" type="email"
              [ngClass]="{ 'is-invalid': f.email.touched && f.email.errors }">
            @if (f.email.touched && f.email.errors) {
              <div class="invalid-feedback d-block">
                @if (f.email.errors.required) {
                  <div>Email is required.</div>
                }
                @if (f.email.errors.email) {
                  <div>Enter a valid email address.</div>
                }
              </div>
            }
          </div>

          <div class="mb-3">
            <label class="form-label" for="guestAccessKey">Access Code</label>
            <input id="guestAccessKey" class="form-control" formControlName="accessKey" placeholder="Enter access code"
              [ngClass]="{ 'is-invalid': f.accessKey.touched && f.accessKey.errors }">
            @if (f.accessKey.touched && f.accessKey.errors) {
              <div class="invalid-feedback d-block">Access code is required.</div>
            }
          </div>

          <button class="btn btn-primary" [disabled]="isSubmitting()">
            @if (isSubmitting()) {
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span class="ms-2">Verifying...</span>
            }
            @if (!isSubmitting()) {
              <span>Continue</span>
              <fa-icon class="ms-2" [icon]="faArrowRight"></fa-icon>
            }
          </button>
        </form>
      }

      @if (requestType() === 'new') {
        <form [formGroup]="newGuestForm" (ngSubmit)="submitNewGuestRequest()">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label" for="firstName">First Name</label>
              <input id="firstName" class="form-control" formControlName="firstname" [ngClass]="{ 'is-invalid': newGuestForm.get('firstname')?.touched && newGuestForm.get('firstname')?.errors }">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="lastName">Last Name</label>
              <input id="lastName" class="form-control" formControlName="lastname" [ngClass]="{ 'is-invalid': newGuestForm.get('lastname')?.touched && newGuestForm.get('lastname')?.errors }">
            </div>
          </div>

          <div class="row g-2 mt-1">
            <div class="col-md-6">
              <label class="form-label" for="guestPhone">Phone Number</label>
              <input id="guestPhone" class="form-control" formControlName="phone" [ngClass]="{ 'is-invalid': newGuestForm.get('phone')?.touched && newGuestForm.get('phone')?.errors }">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="guestEmailRequest">Email</label>
              <input id="guestEmailRequest" class="form-control" type="email" formControlName="email" [ngClass]="{ 'is-invalid': newGuestForm.get('email')?.touched && newGuestForm.get('email')?.errors }">
            </div>
          </div>

          <div class="mt-3">
            <label class="form-label" for="propertyId">Property Name</label>
            <select id="propertyId" class="form-select" formControlName="propertyId" [ngClass]="{ 'is-invalid': newGuestForm.get('propertyId')?.touched && newGuestForm.get('propertyId')?.errors }">
              <option [ngValue]="null">Select property</option>
              @for (property of properties(); track property.id) {
                <option [ngValue]="property.id">{{ property.name }}</option>
              }
            </select>
            @if (isLoadingProperties()) {
              <div class="small text-muted mt-1">Loading properties...</div>
            }
          </div>

          @if (isHomePropertySelected()) {
            <div class="row g-2 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="propertyAccessKey">Access Key</label>
                <input id="propertyAccessKey" class="form-control" formControlName="accesskey">
              </div>
              <div class="col-md-6">
                <label class="form-label" for="propertyIndex">Index</label>
                <input id="propertyIndex" class="form-control" formControlName="index">
              </div>
            </div>
          }

          <div class="mt-3">
            <label class="form-label" for="unitNumber">Unit/Lot Number {{ isHomePropertySelected() ? '(optional)' : '' }}</label>
            <input id="unitNumber" class="form-control" formControlName="unitnumber">
          </div>

          <button class="btn btn-primary mt-3" [disabled]="isSubmittingRequest()">
            @if (isSubmittingRequest()) {
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span class="ms-2">Submitting...</span>
            }
            @if (!isSubmittingRequest()) {
              <span>Submit Request</span>
            }
          </button>
        </form>
      }
    }

    @if (guestAuthenticated()) {
      <div class="mb-3 small text-muted">
        Signed in as <strong>{{ guestUsername() }}</strong>
      </div>

      <ul class="nav nav-tabs mb-3" aria-label="Guest access categories">
        <li class="nav-item">
          <button id="guest-gates-tab" type="button" class="nav-link" [class.active]="selectedDeviceTab() === 'gate'"
            [attr.aria-pressed]="selectedDeviceTab() === 'gate'" aria-controls="guest-gates-panel" (click)="selectedDeviceTab.set('gate')">
            Gates
          </button>
        </li>
        <li class="nav-item">
          <button id="guest-devices-tab" type="button" class="nav-link" [class.active]="selectedDeviceTab() === 'device'"
            [attr.aria-pressed]="selectedDeviceTab() === 'device'" aria-controls="guest-devices-panel" (click)="selectedDeviceTab.set('device')">
            Devices
          </button>
        </li>
        <li class="nav-item">
          <button id="guest-profile-tab" type="button" class="nav-link" [class.active]="selectedDeviceTab() === 'profile'"
            [attr.aria-pressed]="selectedDeviceTab() === 'profile'" aria-controls="guest-profile-panel" (click)="selectedDeviceTab.set('profile')">
            Profile
          </button>
        </li>
      </ul>

      <section id="guest-gates-panel" aria-labelledby="guest-gates-tab" [hidden]="selectedDeviceTab() !== 'gate'">
      @if (sortedDevices().length === 0) {
        <div class="alert alert-secondary mb-0">No gate access is configured for this guest.</div>
      }

      <div class="row g-3">
        @for (device of sortedDevices(); track device.id) {
          <div class="col-md-6">
            <div class="gate-card p-3 h-100">
              <div class="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <h6 class="mb-1">{{ device.labeltxt || ('Gate ' + device.id) }}</h6>
                  <div class="text-muted small">Guest access control</div>
                </div>
                <div class="gate-icon" [ngClass]="isPedestrianGate(device) ? 'ped' : 'veh'">
                  @if (isPedestrianGate(device)) {
                    <fa-icon [icon]="faWalking"></fa-icon>
                  }
                  @if (!isPedestrianGate(device)) {
                    <fa-icon [icon]="faCar"></fa-icon>
                  }
                </div>
              </div>

              <div class="mt-3 d-flex align-items-center justify-content-between">
                <span class="small">Tap to open</span>
                <div class="form-check form-switch m-0">
                  <input class="form-check-input" type="checkbox"
                    [attr.aria-label]="'Open ' + (device.labeltxt || ('Gate ' + device.id))"
                    [checked]="deviceSwitchStates()[$any(device.id)] || false"
                    (change)="toggleGate(device, $any($event.target).checked)">
                </div>
              </div>

              <div class="small mt-2 text-success" *ngIf="deviceSwitchStates()[$any(device.id)]">
                <fa-icon [icon]="faUnlock"></fa-icon>
                <span class="ms-1">Opening...</span>
              </div>
            </div>
          </div>
        }
      </div>
      </section>

      <section id="guest-devices-panel" aria-labelledby="guest-devices-tab" [hidden]="selectedDeviceTab() !== 'device'">
        @if (smartDevices().length > 0) {
          <div class="d-flex justify-content-end mb-3">
            <button type="button" class="btn btn-outline-primary icon-button" title="Refresh device details" aria-label="Refresh device details"
              [disabled]="isRefreshingDevices() || hasPendingDevice()" (click)="loadDeviceDetails()">
              <fa-icon [icon]="faRotateRight"></fa-icon>
            </button>
          </div>
        }
        @if (smartDevices().length === 0) {
          <div class="alert alert-secondary mb-0">No device access is configured for this guest.</div>
        }

        <div class="row g-3">
          @for (device of smartDevices(); track device.id) {
            @let control = deviceControls()[device.id!];
            <div class="col-md-6">
              <div class="gate-card device-card p-3 h-100" [attr.aria-busy]="control?.loading || control?.pending">
                <div class="d-flex align-items-center gap-3">
                  <fa-icon [icon]="deviceIcon(device)" [class.text-success]="control?.isOn" aria-hidden="true"></fa-icon>
                  <div class="device-heading">
                    <h6 class="mb-1">{{ deviceLabel(device) }}</h6>
                    @if (device.description) {
                      <div class="text-muted small">{{ device.description }}</div>
                    }
                  </div>
                </div>
                @if (control?.loading) {
                  <div class="mt-3 small" role="status">
                    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    <span class="ms-2">Loading device...</span>
                  </div>
                }
                @if (control?.details) {
                  <div class="mt-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <span class="small" role="status">{{ control.isOn ? 'On' : 'Off' }}</span>
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                      @if (control.pending) {
                        <span class="spinner-border spinner-border-sm" role="status" aria-label="Sending device command"></span>
                      }
                      @if (control.modes.includes('toggle')) {
                        <div class="form-check form-switch m-0">
                          <input class="form-check-input" type="checkbox" role="switch"
                            [attr.aria-label]="'Toggle ' + deviceLabel(device)" title="Toggle for 5 seconds"
                            [checked]="control.isOn" [disabled]="control.pending || control.loading"
                            (change)="toggleSmartDevice(device, $any($event.target))">
                        </div>
                      }
                      @if (control.modes.includes('delay')) {
                        <button type="button" class="btn btn-outline-primary icon-button" title="Timed operation"
                          [attr.aria-label]="'Timed operation for ' + deviceLabel(device)"
                          [disabled]="control.pending || control.loading" (click)="openDelayDialog(device)">
                          <fa-icon [icon]="faClock"></fa-icon>
                        </button>
                      }
                      @if (control.modes.includes('on/off')) {
                        <button type="button" class="btn icon-button" [ngClass]="control.isOn ? 'btn-success' : 'btn-outline-primary'"
                          [title]="control.isOn ? 'Turn off' : 'Turn on'" [attr.aria-pressed]="control.isOn"
                          [attr.aria-label]="(control.isOn ? 'Turn off ' : 'Turn on ') + deviceLabel(device)"
                          [disabled]="control.pending || control.loading" (click)="operateSmartDevice(device, 'ON/OFF', !control.isOn)">
                          <fa-icon [icon]="faPowerOff"></fa-icon>
                        </button>
                      }
                    </div>
                  </div>
                }
                @if (control?.error) {
                  <div class="text-danger small mt-3" role="alert">{{ control.error }}</div>
                  @if (!control.details) {
                    <button type="button" class="btn btn-outline-primary icon-button mt-2" title="Retry device details"
                      [attr.aria-label]="'Retry details for ' + deviceLabel(device)" [disabled]="control.loading"
                      (click)="loadDeviceDetails(device.id)">
                      <fa-icon [icon]="faRotateRight"></fa-icon>
                    </button>
                  }
                }
              </div>
            </div>
          }
        </div>
      </section>

      <section id="guest-profile-panel" aria-labelledby="guest-profile-tab" [hidden]="selectedDeviceTab() !== 'profile'">
        <dl class="guest-profile mb-0">
          <div class="py-3 border-bottom">
            <dt class="small text-muted">Guest ID</dt>
            <dd class="mb-0">{{ guestProfile().guestid ?? '-' }}</dd>
          </div>
          <div class="py-3 border-bottom">
            <dt class="small text-muted">First Name</dt>
            <dd class="mb-0">{{ guestProfile().firstname || '-' }}</dd>
          </div>
          <div class="py-3 border-bottom">
            <dt class="small text-muted">Last Name</dt>
            <dd class="mb-0">{{ guestProfile().lastname || '-' }}</dd>
          </div>
          <div class="py-3 border-bottom">
            <dt class="small text-muted">Email</dt>
            <dd class="mb-0">{{ guestProfile().email || '-' }}</dd>
          </div>
          <div class="py-3">
            <dt class="small text-muted">Expiration Date</dt>
            <dd class="mb-0">{{ expirationDate() }}</dd>
          </div>
        </dl>
      </section>

      <dialog #deviceDelayDialog class="device-delay-dialog" aria-labelledby="device-delay-title" (cancel)="closeDelayDialog()">
        <form [formGroup]="delayForm" (ngSubmit)="submitDeviceDelay()">
          <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
            <h5 id="device-delay-title" class="mb-0">{{ delayDevice() ? deviceLabel(delayDevice()!) : 'Timed operation' }}</h5>
            <button type="button" class="btn btn-outline-secondary icon-button" title="Close" aria-label="Close" (click)="closeDelayDialog()">
              <fa-icon [icon]="faXmark"></fa-icon>
            </button>
          </div>
          <label class="form-label" for="device-delay-minutes">Operation time (minutes)</label>
          <input id="device-delay-minutes" type="number" class="form-control" formControlName="minutes"
            min="0" max="1440" step="any" autofocus
            [attr.aria-invalid]="delayForm.get('minutes')?.touched && delayForm.get('minutes')?.invalid"
            aria-describedby="device-delay-error">
          <div id="device-delay-error" class="text-danger small mt-2" role="alert">
            @if (delayForm.get('minutes')?.touched && delayForm.get('minutes')?.invalid) {
              Enter a duration between 1 second and 24 hours.
            }
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-outline-secondary" (click)="closeDelayDialog()">Cancel</button>
            <button type="submit" class="btn btn-primary"><fa-icon [icon]="faPlay" class="me-2"></fa-icon>Start</button>
          </div>
        </form>
      </dialog>
    }
  </div>
</div>
